/**
 * JavaScript for the careers search section
 * File location: /assets/careers-search.js
 * 
 * ENHANCED VERSION: Search-only display with STRICT file validation + General CV Submission
 */
document.addEventListener('DOMContentLoaded', function() {
  console.log('Careers search script loaded - Enhanced File Validation + General CV');
  
  // HR Dashboard API Configuration - LOCAL SERVER
  const HR_DASHBOARD_API = 'https://careersadmin.greenteeinc.com.ph';
  
  // FILE VALIDATION SETTINGS - STRICT CONTROLS
  const FILE_VALIDATION = {
    // Allowed file types (case-insensitive)
    allowedTypes: ['.pdf', '.doc', '.docx'],
    // Maximum file size in bytes (5MB)
    maxSizeBytes: 5 * 1024 * 1024,
    // Maximum file size display
    maxSizeDisplay: '5MB',
    // MIME types for additional validation
    allowedMimeTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
  };
  
  // Section wrapper
  const searchSection = document.getElementById('careers-search-section');
  if (!searchSection) {
    console.error('Careers search section not found');
    return;
  }

  // Read schema-controlled colors
  const jobTitleColor  = searchSection.dataset.jobTitleColor   || '';
  const detailsBtnBg   = searchSection.dataset.detailsBtnBg    || '';
  const detailsBtnText = searchSection.dataset.detailsBtnText  || '';
  const applyBtnBg     = searchSection.dataset.applyBtnBg      || '';
  const applyBtnText   = searchSection.dataset.applyBtnText    || '';

  // DOM elements
  const jobSearchInput      = document.getElementById('job-search');
  const locationSearchInput = document.getElementById('location-search');
  const departmentFilter    = document.getElementById('department-filter');
  const searchButton        = document.getElementById('search-jobs-button');
  const resultsContainer    = document.getElementById('search-results');

  // Modal elements
  const modal              = document.getElementById('job-details-modal');
  const closeModalBtn      = document.getElementById('close-job-modal');
  const applicationModal   = document.getElementById('application-form-modal');
  const openApplicationBtn = document.getElementById('open-application-form');
  const closeApplicationBtn = document.getElementById('close-application-form');
  const cancelApplicationBtn = document.getElementById('cancel-application');
  const applicationForm    = document.getElementById('job-application-form');

  // General CV Modal elements
  const generalCvModal = document.getElementById('general-cv-modal');
  const openGeneralCvBtn = document.getElementById('open-general-cv-modal');
  const closeGeneralCvBtn = document.getElementById('close-general-cv');
  const cancelGeneralCvBtn = document.getElementById('cancel-general-cv');
  const generalCvForm = document.getElementById('general-cv-form');

  // Current job data for application
  let currentJobData = null;

  // Department mapping for HR Dashboard data
  const DEPARTMENT_MAP = {
    1: 'Technology',
    2: 'Marketing', 
    3: 'Design',
    4: 'Product',
    5: 'Human Resources',
    6: 'Finance',
    7: 'Operations',
    8: 'Sales'
  };

  // Show initial search prompt - NO AUTO-LOADING
  if (resultsContainer) {
    resultsContainer.innerHTML = '<p class="careers-search-instruction">Enter a job title, keyword, or location to search for opportunities.</p>';
  }

  // 🔍 Fetch & filter jobs - HR Dashboard API Integration
  async function searchJobs() {
    console.log('searchJobs function called');
    
    if (!jobSearchInput || !locationSearchInput || !departmentFilter || !resultsContainer) {
      console.error('Required elements not found');
      return;
    }

    const jobQ       = jobSearchInput.value.trim();
    const locationQ  = locationSearchInput.value.trim();
    const departmentQ = departmentFilter.value.trim();

    console.log('Search parameters:', { jobQ, locationQ, departmentQ });

    // Require at least one search term
    if (!jobQ && !locationQ && !departmentQ) {
      resultsContainer.innerHTML =
        '<p class="careers-search-instruction">Please enter a job title, keyword, location, or select a department.</p>';
      return;
    }

    // Loading spinner
    resultsContainer.innerHTML = `
      <div class="careers-loading">
        <div class="careers-spinner"></div>
        <p>Searching for opportunities...</p>
      </div>
    `;

    try {
      console.log('Fetching from HR Dashboard API:', `${HR_DASHBOARD_API}/api/jobs`);
      
      const response = await fetch(`${HR_DASHBOARD_API}/api/jobs`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('HR Dashboard API Response:', data);
      
      // Handle API response format
      let jobs = [];
      if (Array.isArray(data)) {
        jobs = data;
      } else if (data.jobs && Array.isArray(data.jobs)) {
        jobs = data.jobs;
      } else if (data.data && Array.isArray(data.data)) {
        jobs = data.data;
      }
      
      console.log('Jobs received from HR Dashboard:', jobs.length);
      
      // Filter jobs based on search criteria
      if (jobQ || locationQ || departmentQ) {
        const originalCount = jobs.length;
        jobs = jobs.filter(job => {
          const matchesJob = !jobQ || 
            job.title?.toLowerCase().includes(jobQ.toLowerCase()) ||
            job.description?.toLowerCase().includes(jobQ.toLowerCase()) ||
            job.requirements?.toLowerCase().includes(jobQ.toLowerCase());
          
          const matchesLocation = !locationQ || 
            job.location?.toLowerCase().includes(locationQ.toLowerCase());
          
          // Enhanced department matching
          const departmentName = DEPARTMENT_MAP[job.department_id] || '';
          const matchesDepartment = !departmentQ || 
            job.department_id?.toString() === departmentQ ||
            departmentName?.toLowerCase().includes(departmentQ.toLowerCase()) ||
            departmentQ.toLowerCase().replace(/\s+/g, '-') === departmentName?.toLowerCase().replace(/\s+/g, '-');
          
          // Only show active jobs
          const isActive = !job.status || job.status === 'ACTIVE';
          
          return matchesJob && matchesLocation && matchesDepartment && isActive;
        });
        console.log(`Filtered from ${originalCount} to ${jobs.length} jobs`);
      }
      
      // Convert HR Dashboard format to display format (matching your original design)
      const formattedJobs = jobs.map(job => ({
        id: job.id,
        title: job.title || 'Untitled Position',
        location: job.location || 'Location TBD',
        department: DEPARTMENT_MAP[job.department_id] || 'General',
        description: job.description || 'Job description will be provided during the interview process.',
        requirements: formatRequirements(job.requirements),
        salary_range: job.salary_range,
        created_at: job.created_at
      }));
      
      console.log('Formatted jobs for display:', formattedJobs.length);
      displayResults({ jobs: formattedJobs, total: formattedJobs.length });
      
    } catch (error) {
      console.error('HR Dashboard API error:', error);
      
      // Show error message
      resultsContainer.innerHTML = `
        <div class="careers-error">
          <h3>Unable to load job opportunities</h3>
          <p><strong>Error:</strong> ${error.message}</p>
          <p>Please make sure your HR Dashboard is running on <code>http://localhost:3000</code></p>
          <button onclick="location.reload()" 
                  style="margin-top: 15px; padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Try Again
          </button>
        </div>
      `;
    }
  }

  // Format requirements for display
  function formatRequirements(requirements) {
    if (!requirements) return ['Requirements will be discussed during the interview'];
    
    if (typeof requirements === 'string') {
      // Split by common delimiters
      const split = requirements.split(/[\n\r\*\-•]+/).filter(r => r.trim());
      return split.length > 1 ? split.map(r => r.trim()) : [requirements.trim()];
    }
    
    if (Array.isArray(requirements)) {
      return requirements.filter(r => r && r.trim());
    }
    
    return [requirements.toString()];
  }

  // 📋 Render cards with clean design (matching your original)
  function displayResults(data) {
    if (!data.jobs.length) {
      resultsContainer.innerHTML =
        '<p class="careers-no-results">No opportunities found matching your criteria. Try adjusting your search.</p>';
      return;
    }

    let html = `
      <div class="search-results-header">
        <p>${data.jobs.length} opportunities found</p>
      </div>
      <div class="jobs-list">
    `;

    data.jobs.forEach(job => {
      html += `
        <div class="job-card" data-job-id="${job.id}">
          <div class="job-content">
            <h3 class="job-title" style="color:${jobTitleColor}">${job.title}</h3>
            <p class="job-location">${job.location}</p>
            <p class="job-department">${job.department}</p>
          </div>
          <div class="job-apply">
            <button
              type="button"
              class="job-apply-button view-details-btn"
              data-job-id="${job.id}"
              style="background-color:${detailsBtnBg}; color:${detailsBtnText};"
            >View details</button>
          </div>
        </div>
      `;
    });

    html += '</div>';
    resultsContainer.innerHTML = html;

    // Card click (outside button) opens modal
    document.querySelectorAll('.job-card').forEach(card => {
      card.addEventListener('click', e => {
        if (!e.target.closest('.view-details-btn')) {
          showJobDetails(card.dataset.jobId, data.jobs);
        }
      });
    });

    // Button click opens modal
    document.querySelectorAll('.view-details-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        showJobDetails(btn.dataset.jobId, data.jobs);
      });
    });
  }

  // 🔍 Show job details modal (matching your original design)
  function showJobDetails(jobId, jobsList) {
    const job = jobsList.find(j => j.id == jobId);
    if (!job) {
      console.error('Job not found:', jobId);
      return;
    }

    // Store current job data for application form
    currentJobData = job;

    console.log('Showing job details for:', job.title);

    // Populate modal with job details
    document.getElementById('modal-job-title').textContent = job.title;
    document.getElementById('modal-job-location').textContent = `Location: ${job.location}`;
    document.getElementById('modal-job-department').textContent = `Department: ${job.department}`;
    document.getElementById('modal-job-description').innerHTML = decodeHTMLEntities(job.description);
    
    // Format requirements as list items
    const requirementsList = document.getElementById('modal-job-requirements');
    requirementsList.innerHTML = '';
    if (Array.isArray(job.requirements)) {
      job.requirements.forEach(req => {
        const li = document.createElement('li');
        li.innerHTML = decodeHTMLEntities(req);
        requirementsList.appendChild(li);
      });
    } else {
      const li = document.createElement('li');
      li.innerHTML = decodeHTMLEntities(job.requirements);
      requirementsList.appendChild(li);
    }

    modal.style.display = 'block';
    document.body.style.overflow = 'hidden';
  }

  function decodeHTMLEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  // 📝 Show application form with enhanced file requirements display
  function showApplicationForm() {
    if (!currentJobData) {
      console.error('No job data available for application');
      return;
    }
    
    console.log('Opening application form for:', currentJobData.title);
    
    document.getElementById('application-form-title').textContent = `Apply for ${currentJobData.title}`;
    
    // Hide job details modal and show application form
    modal.style.display = 'none';
    applicationModal.style.display = 'block';
    
    // Reset form
    applicationForm.reset();
    
    // Reset file upload displays
    document.querySelectorAll('.file-upload-filename').forEach(div => {
      div.classList.remove('show');
    });
    document.querySelectorAll('.file-upload-display').forEach(display => {
      display.style.borderColor = '#ddd';
      display.style.backgroundColor = '#fafafa';
    });
    
    // Set minimum date to today
    const today = new Date().toISOString().split('T')[0];
    const availabilityField = document.getElementById('availability');
    if (availabilityField) {
      availabilityField.min = today;
    }
    
    // Setup enhanced file upload handlers with strict validation
    setupEnhancedFileUploadHandlers();
    
    // Display file requirements prominently
    displayFileRequirements();
  }

  // 🆕 Show General CV submission modal
  function showGeneralCvModal() {
    console.log('Opening general CV submission modal');
    
    generalCvModal.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Reset form
    generalCvForm.reset();
    
    // Reset file upload displays
    const filenameDiv = document.getElementById('general-resume-filename');
    const display = document.querySelector('#general-cv-modal .file-upload-display');
    
    if (filenameDiv) {
      filenameDiv.classList.remove('show');
      filenameDiv.textContent = '';
    }
    if (display) {
      display.style.borderColor = '#ddd';
      display.style.backgroundColor = '#fafafa';
    }
    
    // Setup file upload handlers for general CV
    setupGeneralCvFileUploadHandlers();
    
    // Display file requirements prominently
    displayGeneralCvFileRequirements();
  }

  // 📋 Display file requirements prominently in the application form
  function displayFileRequirements() {
    const fileRequirementsHtml = `
      <div class="file-requirements-notice" style="
        background: #f0f9ff; 
        border: 2px solid #0ea5e9; 
        border-radius: 8px; 
        padding: 16px; 
        margin: 16px 0; 
        font-size: 14px;
      ">
        <h4 style="color: #0369a1; margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">📎 File Upload Requirements</h4>
        <ul style="margin: 8px 0; padding-left: 20px; color: #374151;">
          <li><strong>Accepted formats:</strong> PDF, DOC, DOCX only</li>
          <li><strong>Maximum file size:</strong> ${FILE_VALIDATION.maxSizeDisplay} per file</li>
          <li><strong>Resume:</strong> Required for all applications</li>
          <li><strong>Cover Letter:</strong> Optional but recommended</li>
        </ul>
        <p style="margin: 8px 0 0 0; color: #dc2626; font-weight: 500;">
          ⚠️ Files that don't meet these requirements will be automatically rejected.
        </p>
      </div>
    `;
    
    // Insert requirements notice after the form title or at the beginning of the form
    const formTitle = document.getElementById('application-form-title');
    if (formTitle && formTitle.parentNode && !document.querySelector('.file-requirements-notice')) {
      const requirementsDiv = document.createElement('div');
      requirementsDiv.innerHTML = fileRequirementsHtml;
      formTitle.parentNode.insertBefore(requirementsDiv, formTitle.nextSibling);
    }
  }

  // 🆕 Display file requirements for general CV modal
  function displayGeneralCvFileRequirements() {
    const fileRequirementsHtml = `
      <div class="file-requirements-notice" style="
        background: #f0f9ff; 
        border: 2px solid #0ea5e9; 
        border-radius: 8px; 
        padding: 16px; 
        margin: 16px 0; 
        font-size: 14px;
      ">
        <h4 style="color: #0369a1; margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">📎 File Upload Requirements</h4>
        <ul style="margin: 8px 0; padding-left: 20px; color: #374151;">
          <li><strong>Accepted formats:</strong> PDF, DOC, DOCX only</li>
          <li><strong>Maximum file size:</strong> ${FILE_VALIDATION.maxSizeDisplay}</li>
          <li><strong>Resume/CV:</strong> Required</li>
        </ul>
        <p style="margin: 8px 0 0 0; color: #dc2626; font-weight: 500;">
          ⚠️ Files that don't meet these requirements will be automatically rejected.
        </p>
      </div>
    `;
    
    // Insert requirements notice after the form title
    const formTitle = document.getElementById('general-cv-title');
    if (formTitle && formTitle.parentNode && !document.querySelector('#general-cv-modal .file-requirements-notice')) {
      const requirementsDiv = document.createElement('div');
      requirementsDiv.innerHTML = fileRequirementsHtml;
      formTitle.parentNode.insertBefore(requirementsDiv, formTitle.nextSibling);
    }
  }

  // 🛡️ ENHANCED FILE VALIDATION FUNCTIONS
  function validateFileType(file) {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    
    // Check file extension
    if (!FILE_VALIDATION.allowedTypes.includes(fileExtension)) {
      return {
        valid: false,
        error: `Invalid file type: ${fileExtension}\nAllowed types: ${FILE_VALIDATION.allowedTypes.join(', ')}`
      };
    }
    
    // Check MIME type for additional security
    if (!FILE_VALIDATION.allowedMimeTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file format detected.\nPlease ensure you're uploading a genuine ${FILE_VALIDATION.allowedTypes.join('/')} file.`
      };
    }
    
    return { valid: true };
  }

  function validateFileSize(file) {
    if (file.size > FILE_VALIDATION.maxSizeBytes) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return {
        valid: false,
        error: `File too large: ${fileSizeMB}MB\nMaximum allowed: ${FILE_VALIDATION.maxSizeDisplay}`
      };
    }
    
    return { valid: true };
  }

  function validateFileName(file) {
    const fileName = file.name;
    
    // Check for potentially harmful file names
    const dangerousPatterns = [/\.exe$/i, /\.bat$/i, /\.cmd$/i, /\.scr$/i, /\.vbs$/i];
    if (dangerousPatterns.some(pattern => pattern.test(fileName))) {
      return {
        valid: false,
        error: 'File name contains potentially unsafe characters.'
      };
    }
    
    // Check file name length
    if (fileName.length > 255) {
      return {
        valid: false,
        error: 'File name is too long. Please rename your file to be shorter than 255 characters.'
      };
    }
    
    return { valid: true };
  }

  function performCompleteFileValidation(file, fileType) {
    console.log(`🔍 Validating ${fileType}:`, file.name, `(${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    
    // Validate file type
    const typeValidation = validateFileType(file);
    if (!typeValidation.valid) {
      return typeValidation;
    }
    
    // Validate file size
    const sizeValidation = validateFileSize(file);
    if (!sizeValidation.valid) {
      return sizeValidation;
    }
    
    // Validate file name
    const nameValidation = validateFileName(file);
    if (!nameValidation.valid) {
      return nameValidation;
    }
    
    console.log(`✅ ${fileType} validation passed:`, file.name);
    return { valid: true };
  }

  // 📎 Enhanced file upload handlers with strict validation
  function setupEnhancedFileUploadHandlers() {
    const fileInputs = [
      { id: 'applicant-resume', name: 'Resume', required: true },
      { id: 'applicant-cover-letter', name: 'Cover Letter', required: false }
    ];
    
    fileInputs.forEach(({ id, name, required }) => {
      const input = document.getElementById(id);
      const wrapper = input?.closest('.file-upload-wrapper');
      const display = wrapper?.querySelector('.file-upload-display');
      const filenameDiv = wrapper?.querySelector('.file-upload-filename');
      
      if (!input || !wrapper || !display || !filenameDiv) {
        console.warn(`File input setup incomplete for ${id}`);
        return;
      }

      // Add file requirements to the display area
      if (display) {
        const requirementsText = display.querySelector('.file-requirements');
        if (!requirementsText) {
          const reqDiv = document.createElement('div');
          reqDiv.className = 'file-requirements';
          reqDiv.innerHTML = `
            <small style="color: #6b7280; display: block; margin-top: 4px;">
              ${required ? '* Required' : 'Optional'} • PDF, DOC, DOCX only • Max ${FILE_VALIDATION.maxSizeDisplay}
            </small>
          `;
          display.appendChild(reqDiv);
        }
      }

      // Enhanced file change handler with strict validation
      input.addEventListener('change', function(e) {
        const file = e.target.files[0];
        
        if (!file) {
          resetFileDisplay(display, filenameDiv);
          return;
        }
        
        // Perform complete validation
        const validation = performCompleteFileValidation(file, name);
        
        if (!validation.valid) {
          // Show detailed error message
          showFileError(validation.error, name);
          
          // Clear the invalid file
          input.value = '';
          resetFileDisplay(display, filenameDiv);
          return;
        }
        
        // File is valid - update display
        updateFileDisplay(file, display, filenameDiv, name);
      });

      // Setup drag and drop
      setupDragAndDrop(display, input, name);
    });
  }

  // 🆕 Setup file upload handlers for general CV modal
  function setupGeneralCvFileUploadHandlers() {
    const input = document.getElementById('general-resume');
    const wrapper = input?.closest('.file-upload-wrapper');
    const display = wrapper?.querySelector('.file-upload-display');
    const filenameDiv = document.getElementById('general-resume-filename');
    
    if (!input || !wrapper || !display || !filenameDiv) {
      console.warn('General CV file input setup incomplete');
      return;
    }

    // Add file requirements to the display area
    if (display) {
      const requirementsText = display.querySelector('.file-requirements');
      if (!requirementsText) {
        const reqDiv = document.createElement('div');
        reqDiv.className = 'file-requirements';
        reqDiv.innerHTML = `
          <small style="color: #6b7280; display: block; margin-top: 4px;">
            * Required • PDF, DOC, DOCX only • Max ${FILE_VALIDATION.maxSizeDisplay}
          </small>
        `;
        display.appendChild(reqDiv);
      }
    }

    // Enhanced file change handler with strict validation
    input.addEventListener('change', function(e) {
      const file = e.target.files[0];
      
      if (!file) {
        resetFileDisplay(display, filenameDiv);
        return;
      }
      
      // Perform complete validation
      const validation = performCompleteFileValidation(file, 'Resume');
      
      if (!validation.valid) {
        // Show detailed error message
        showFileError(validation.error, 'Resume');
        
        // Clear the invalid file
        input.value = '';
        resetFileDisplay(display, filenameDiv);
        return;
      }
      
      // File is valid - update display
      updateFileDisplay(file, display, filenameDiv, 'Resume');
    });

    // Setup drag and drop
    setupDragAndDrop(display, input, 'Resume');
  }

  // Setup drag and drop functionality
  function setupDragAndDrop(display, input, fileTypeName) {
    display.addEventListener('dragover', function(e) {
      e.preventDefault();
      display.classList.add('dragover');
    });

    display.addEventListener('dragleave', function(e) {
      e.preventDefault();
      display.classList.remove('dragover');
    });

    display.addEventListener('drop', function(e) {
      e.preventDefault();
      display.classList.remove('dragover');
      
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        
        // Validate dropped file
        const validation = performCompleteFileValidation(file, fileTypeName);
        
        if (validation.valid) {
          // Create a new FileList and assign it to the input
          const dt = new DataTransfer();
          dt.items.add(file);
          input.files = dt.files;
          
          // Trigger change event
          const event = new Event('change', { bubbles: true });
          input.dispatchEvent(event);
        } else {
          showFileError(validation.error, fileTypeName);
        }
      }
    });
  }

  function resetFileDisplay(display, filenameDiv) {
    filenameDiv.classList.remove('show');
    filenameDiv.textContent = '';
    display.style.borderColor = '#ddd';
    display.style.backgroundColor = '#fafafa';
  }

  function updateFileDisplay(file, display, filenameDiv, fileTypeName) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    filenameDiv.innerHTML = `
      <span style="color: #059669; font-weight: 500;">✅ ${fileTypeName} Selected</span><br>
      <span style="color: #374151; font-size: 12px;">${file.name} (${fileSizeMB}MB)</span>
    `;
    filenameDiv.classList.add('show');
    display.style.borderColor = '#059669';
    display.style.backgroundColor = '#ecfdf5';
  }

  function showFileError(errorMessage, fileTypeName) {
    // Create and show error modal/alert
    const errorHtml = `
      <div style="
        position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
        background: white; border: 3px solid #dc2626; border-radius: 12px;
        padding: 24px; max-width: 400px; z-index: 10000;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
      ">
        <div style="text-align: center;">
          <div style="color: #dc2626; font-size: 48px; margin-bottom: 16px;">⚠️</div>
          <h3 style="color: #dc2626; margin: 0 0 12px 0; font-size: 18px;">Invalid ${fileTypeName}</h3>
          <p style="color: #374151; margin: 0 0 16px 0; line-height: 1.5;">${errorMessage}</p>
          <button onclick="this.parentElement.parentElement.remove()" style="
            background: #dc2626; color: white; border: none; 
            padding: 8px 20px; border-radius: 6px; cursor: pointer;
            font-size: 14px; font-weight: 500;
          ">OK</button>
        </div>
      </div>
      <div style="
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0, 0, 0, 0.5); z-index: 9999;
      " onclick="this.nextElementSibling.remove(); this.remove();"></div>
    `;
    
    const errorDiv = document.createElement('div');
    errorDiv.innerHTML = errorHtml;
    document.body.appendChild(errorDiv);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 10000);
  }

  // 📝 Enhanced form submission with file validation
  function handleFormSubmission(e) {
    e.preventDefault();
    
    console.log('Form submission started for job:', currentJobData.title);
    
    // Final file validation before submission
    const resumeFile = document.getElementById('applicant-resume').files[0];
    const coverLetterFile = document.getElementById('applicant-cover-letter').files[0];
    
    // Validate required resume
    if (!resumeFile) {
      showFileError('Resume is required for all job applications.', 'Resume');
      return;
    }
    
    // Re-validate resume file
    const resumeValidation = performCompleteFileValidation(resumeFile, 'Resume');
    if (!resumeValidation.valid) {
      showFileError(resumeValidation.error, 'Resume');
      return;
    }
    
    // Validate cover letter if provided
    if (coverLetterFile) {
      const coverLetterValidation = performCompleteFileValidation(coverLetterFile, 'Cover Letter');
      if (!coverLetterValidation.valid) {
        showFileError(coverLetterValidation.error, 'Cover Letter');
        return;
      }
    }
    
    // Validate required terms agreement
    const termsAgreed = document.getElementById('terms-agreement').checked;
    if (!termsAgreed) {
      alert('Please accept the Terms and Conditions to continue');
      return;
    }

    // Create FormData with proper field mapping for your HR Dashboard API
    const formData = new FormData();
    
    // Add job data
    formData.append('jobId', currentJobData.id);
    formData.append('jobTitle', currentJobData.title);
    formData.append('jobDepartment', currentJobData.department);
    formData.append('jobLocation', currentJobData.location);
    
    // Add form fields (using exact field names from your API)
    formData.append('name', document.getElementById('applicant-name').value);
    formData.append('email', document.getElementById('applicant-email').value);
    formData.append('phone', document.getElementById('applicant-phone').value);
    formData.append('location', document.getElementById('applicant-location').value);
    formData.append('experience', document.getElementById('years-experience').value);
    formData.append('start_date', document.getElementById('availability').value);
    formData.append('salary', document.getElementById('salary-expectation').value);
    formData.append('source', document.getElementById('how-did-you-hear').value);
    formData.append('motivation', document.getElementById('additional-info').value);
    formData.append('terms_agreed', termsAgreed ? 'on' : 'off');
    formData.append('marketing_consent', document.getElementById('marketing-consent').checked ? 'on' : 'off');
    
    // Add validated files
    formData.append('resume', resumeFile);
    if (coverLetterFile) {
      formData.append('cover_letter', coverLetterFile);
    }

    console.log('Submitting application to HR Dashboard with validated files...');

    // Show loading state
    const submitBtn = document.querySelector('#job-application-form .btn-primary');
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Submitting...';
    submitBtn.disabled = true;

    // Submit to HR Dashboard API
    fetch(`${HR_DASHBOARD_API}/api/applications`, {
      method: 'POST',
      body: formData
    })
    .then(response => {
      console.log('Response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('Application submitted successfully:', data);
      
      if (data.success || data.applicationId) {
        alert(`Thank you! Your application for "${currentJobData.title}" has been submitted successfully.`);
        closeAllModals();
      } else {
        throw new Error(data.error || 'Application submission failed');
      }
    })
    .catch(error => {
      console.error('Application submission error:', error);
      alert(`Failed to submit application: ${error.message}\n\nPlease ensure your HR Dashboard is running and try again.`);
    })
    .finally(() => {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    });
  }

  // 🆕 Handle General CV form submission
  function handleGeneralCvSubmission(e) {
    e.preventDefault();
    
    console.log('General CV submission started');
    
    // Final file validation before submission
    const resumeFile = document.getElementById('general-resume').files[0];
    
    // Validate required resume
    if (!resumeFile) {
      showFileError('Resume is required for CV submission.', 'Resume');
      return;
    }
    
    // Re-validate resume file
    const resumeValidation = performCompleteFileValidation(resumeFile, 'Resume');
    if (!resumeValidation.valid) {
      showFileError(resumeValidation.error, 'Resume');
      return;
    }
    
    // Validate required terms agreement
    const termsAgreed = document.getElementById('general-terms-agreement').checked;
    if (!termsAgreed) {
      alert('Please accept the Terms and Conditions to continue');
      return;
    }

    // Create FormData for general CV submission
    const formData = new FormData();
    
    // Add form fields
    formData.append('type', 'general_cv');
    formData.append('name', document.getElementById('general-name').value);
    formData.append('email', document.getElementById('general-email').value);
    formData.append('phone', document.getElementById('general-phone').value);
    formData.append('location', document.getElementById('general-location').value);
    formData.append('motivation', document.getElementById('general-interest').value);
    formData.append('terms_agreed', termsAgreed ? 'on' : 'off');
    formData.append('marketing_consent', document.getElementById('general-marketing-consent').checked ? 'on' : 'off');
    
    // Add validated file
    formData.append('resume', resumeFile);

    console.log('Submitting general CV to HR Dashboard...');

    // Show loading state
    const submitBtn = document.querySelector('#general-cv-form .btn-primary');
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Submitting...';
    submitBtn.disabled = true;

    // Submit to HR Dashboard API (same endpoint, different type)
    fetch(`${HR_DASHBOARD_API}/api/applications`, {
      method: 'POST',
      body: formData
    })
    .then(response => {
      console.log('Response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('General CV submitted successfully:', data);
      
      if (data.success || data.applicationId) {
        alert('Thank you! Your CV has been submitted successfully. We\'ll keep you in mind for future opportunities that match your profile.');
        closeGeneralCvModal();
      } else {
        throw new Error(data.error || 'CV submission failed');
      }
    })
    .catch(error => {
      console.error('General CV submission error:', error);
      alert(`Failed to submit CV: ${error.message}\n\nPlease ensure your HR Dashboard is running and try again.`);
    })
    .finally(() => {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    });
  }

  // Modal functions
  function hideApplicationForm() {
    applicationModal.style.display = 'none';
    modal.style.display = 'block';
    
    // Remove file requirements notice
    const requirementsNotice = document.querySelector('.file-requirements-notice');
    if (requirementsNotice) {
      requirementsNotice.remove();
    }
  }

  function closeGeneralCvModal() {
    generalCvModal.style.display = 'none';
    document.body.style.overflow = '';
    
    // Remove file requirements notice
    const requirementsNotice = document.querySelector('#general-cv-modal .file-requirements-notice');
    if (requirementsNotice) {
      requirementsNotice.remove();
    }
  }

  function closeAllModals() {
    modal.style.display = 'none';
    applicationModal.style.display = 'none';
    generalCvModal.style.display = 'none';
    document.body.style.overflow = '';
    currentJobData = null;
    
    // Remove file requirements notices
    document.querySelectorAll('.file-requirements-notice').forEach(notice => {
      notice.remove();
    });
  }

  // Event listeners for job application
  if (closeModalBtn) closeModalBtn.addEventListener('click', closeAllModals);
  if (openApplicationBtn) openApplicationBtn.addEventListener('click', showApplicationForm);
  if (closeApplicationBtn) closeApplicationBtn.addEventListener('click', hideApplicationForm);
  if (cancelApplicationBtn) cancelApplicationBtn.addEventListener('click', hideApplicationForm);
  if (applicationForm) applicationForm.addEventListener('submit', handleFormSubmission);

  // 🆕 Event listeners for general CV submission
  if (openGeneralCvBtn) {
    openGeneralCvBtn.addEventListener('click', showGeneralCvModal);
  }
  if (closeGeneralCvBtn) {
    closeGeneralCvBtn.addEventListener('click', closeGeneralCvModal);
  }
  if (cancelGeneralCvBtn) {
    cancelGeneralCvBtn.addEventListener('click', closeGeneralCvModal);
  }
  if (generalCvForm) {
    generalCvForm.addEventListener('submit', handleGeneralCvSubmission);
  }
  
  window.addEventListener('click', e => {
    if (e.target === modal) closeAllModals();
    else if (e.target === applicationModal) hideApplicationForm();
    else if (e.target === generalCvModal) closeGeneralCvModal();
  });

  // 🔍 Search event listeners - NO AUTO-LOADING
  if (searchButton) {
    console.log('Attaching click event to search button');
    searchButton.addEventListener('click', function() {
      console.log('Search button clicked!');
      searchJobs();
    });
  }

  if (jobSearchInput) {
    jobSearchInput.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        console.log('Enter pressed in job search input');
        searchJobs();
      }
    });
  }

  if (locationSearchInput) {
    locationSearchInput.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        console.log('Enter pressed in location search input');
        searchJobs();
      }
    });
  }

  if (departmentFilter) {
    departmentFilter.addEventListener('change', function() {
      console.log('Department filter changed');
      searchJobs();
    });
  }

  console.log('Careers search script initialization complete - ENHANCED FILE VALIDATION + GENERAL CV');
  console.log(`HR Dashboard API: ${HR_DASHBOARD_API}`);
  console.log('File validation settings:', FILE_VALIDATION);
});